import styled, { css, keyframes } from 'styled-components';

/* Visual tokens — ported from the PandaDocAnalytics page so the Finances
 * section feels like one product. Kept local to this folder to avoid
 * coupling across pages — duplication is cheap and stable. */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  vertical-align: -1px;
  flex-shrink: 0;
`;

export const Wrapper = styled.div`
  width: 100%;
  margin: 32px 0 0;
  color: var(--k-fg-0f172a);
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

export const BackLink = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: var(--k-fg-2563eb);
  cursor: pointer;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

export const SubTitle = styled.div`
  color: var(--k-fg-64748b);
  font-size: 13px;
  margin-bottom: 16px;
`;

export const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  border-bottom: 1px solid var(--k-border-e2e8f0);
  margin: 16px 0 4px;
`;

export const TabBarItem = styled.button<{ active: boolean }>`
  border: 0;
  background: transparent;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) =>
    active ? 'var(--k-fg-0f172a)' : 'var(--k-fg-64748b)'};
  border-bottom: 2px solid
    ${({ active }) => (active ? 'var(--k-border-0f172a)' : 'transparent')};
  margin-bottom: -1px;
  transition: color 0.15s;

  &:hover {
    color: var(--k-fg-0f172a);
  }
`;

export const HeaderRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const UnmatchedBadge = styled.button`
  border: 0;
  background: var(--k-bg-fff7ed);
  color: var(--k-fg-9a3412);
  border: 1px solid var(--k-border-fed7aa);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--k-bg-ffedd5);
  }
`;

export const FilterBar = styled.div`
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 16px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const FilterLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-64748b);
  font-weight: 600;
`;

export const SegmentTabs = styled.div`
  display: inline-flex;
  /* ТЗ 0103: длинная полоса (10 периодов маркетинга) переносится внутри
     себя, а не уводит страницу вбок на узком экране. */
  flex-wrap: wrap;
  background: var(--k-bg-f1f5f9);
  border-radius: 8px;
  padding: 3px;
`;

export const SegmentTab = styled.button<{ active: boolean }>`
  /* Значок внутри сегмента: у кнопки по умолчанию не flex, и глиф садился бы
     на базовую линию текста, а не по центру подписи. */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: ${({ active }) =>
    active ? 'var(--k-bg-ffffff)' : 'transparent'};
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ active }) =>
    active ? 'var(--k-fg-0f172a)' : 'var(--k-fg-64748b)'};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s;
  box-shadow: ${({ active }) =>
    active ? '0 1px 3px var(--k-shadow-000000-a80)' : 'none'};

  /* ТЗ 0103: недоступный сегмент («День» на окне > 92 дней) должен
     выглядеть недоступным, а не сломанным. */
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--k-border-2563eb);
    outline-offset: 1px;
  }
`;

export const NativeSelect = styled.select`
  height: 32px;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  background: var(--k-bg-ffffff);
  outline: none;

  &:focus {
    border-color: var(--k-border-2563eb);
  }
`;

export const NativeInput = styled.input`
  height: 32px;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
  background: var(--k-bg-ffffff);
  outline: none;
  min-width: 220px;

  &:focus {
    border-color: var(--k-border-2563eb);
  }
`;

export const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: var(--k-fg-64748b);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  text-decoration: underline;

  &:hover {
    color: var(--k-fg-0f172a);
  }

  /* Выключенная кнопка обязана ВЫГЛЯДЕТЬ выключенной: свои color и cursor
     перебивали браузерный стиль, а :hover продолжал срабатывать — кнопка
     подсвечивалась под курсором и молча ничего не делала. */
  /* 🔴 Цвет обязан ОТЛИЧАТЬСЯ от включённого состояния. Первый заход поставил
     сюда #5b6775 против #64748b — на экране это один и тот же серый, и
     выключенная кнопка выглядела рабочей. Заодно снимаем подчёркивание: оно
     обещает действие. */
  &:disabled {
    color: var(--k-fg-a8b1bd);
    text-decoration: none;
    cursor: not-allowed;
  }

  &:disabled:hover {
    color: var(--k-fg-a8b1bd);
  }
`;

export const Banner = styled.div<{ variant?: 'warn' | 'success' | 'info' }>`
  border-radius: 10px;
  padding: 12px 16px;
  margin: 8px 0 16px;
  font-size: 13px;
  line-height: 1.5;
  ${({ variant }) => {
    if (variant === 'success') {
      return css`
        border: 1px solid var(--k-border-bbf7d0);
        background: var(--k-bg-f0fdf4);
        color: var(--k-fg-166534);
      `;
    }
    if (variant === 'info') {
      // Phase 12: «part of order» banner uses a blue palette to read as
      // informational rather than an alert — sibling to «correction of».
      return css`
        border: 1px solid var(--c-info-banner-line);
        background: var(--c-info-banner);
        color: var(--k-fg-1e40af);
      `;
    }
    return css`
      border: 1px solid var(--k-border-fed7aa);
      background: var(--k-bg-fff7ed);
      color: var(--k-fg-9a3412);
    `;
  }}
`;

export const BannerTitle = styled.div`
  font-weight: 700;
  margin-bottom: 2px;
`;

/** Inline retry inside a warn banner (drain error surfacing — audit). */
export const BannerRetry = styled.button`
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  border-radius: 6px;
  padding: 3px 10px;
  margin-top: 4px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  overflow: hidden;

  thead th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--k-fg-64748b);
    font-weight: 600;
    padding: 10px 12px;
    border-bottom: 1px solid var(--k-border-e2e8f0);
    background: var(--k-bg-f8fafc);
  }

  tbody td {
    padding: 12px;
    border-bottom: 1px solid var(--k-border-f1f5f9);
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.1s;
  }

  tbody tr:hover {
    background: var(--k-bg-f8fafc);
  }
`;

export const NameCell = styled.td`
  font-weight: 600;
  color: var(--k-fg-0f172a);
`;

export const TagCell = styled.td`
  white-space: nowrap;
`;

/**
 * Растущая текстовая колонка (одна на таблицу): забирает слабину ширины и
 * переносит длинный e-mail, вместо того чтобы выталкивать последние колонки
 * за край обёртки. Перепроверка G44 — «Счета по группе» на 1440.
 */
/**
 * «Привязка» (● дата · сумма взноса). Рядом с растущей колонкой авторазметка
 * сжимает её до минимума — на широком экране держим одной строкой, на узком
 * (карточка группы на 1100) дата и сумма встают в две строки вместо того,
 * чтобы уехать за край. Перепроверка G44.
 */
export const LinkCell = styled.td`
  @media (min-width: 1280px) {
    white-space: nowrap;
  }
`;

export const GrowTextCell = styled.td.attrs({ 'data-grow': 'true' })`
  width: 100%;
  min-width: 140px;
  overflow-wrap: anywhere;
`;

export const RowActionsCell = styled.td`
  text-align: right;
  white-space: nowrap;
`;

/**
 * Кнопка действия внутри строки таблицы.
 *
 * 🔴 `white-space: nowrap` и `flex-shrink: 0` — не оформление, а починка.
 * `atoms/Table` объявляет на таблице `word-break: break-word` (Table.tsx:136),
 * а это свойство наследуемое и вычисляется в `overflow-wrap: anywhere`. Оно
 * убирает у подписи минимальный размер по самому длинному слову — тот самый
 * пол, который обычно не даёт кнопке сжаться. В ячейке шириной 150px кнопка
 * после этого ужимается почти до буквы, и оператор читает «Правит / ь»,
 * «Скрыт / ь», «Снять с / продажи». `nowrap` возвращает пол в листе, не
 * трогая таблицу, на которой стоят ещё 19 страниц.
 *
 * Это уже трижды обходили локальными `ActionRow` (`SkillsDictionary`,
 * `ExerciseTemplateEditor`, `PromosTab`) — значит место починки здесь.
 *
 * `:disabled` — потому что 26 файлов передают сюда `disabled={pending}`, и до
 * сих пор это не отражалось никак: кнопка выглядела живой, пока шло
 * сохранение. Правило скопировано у соседа `L.PrimaryActionButton`.
 *
 * ⚠️ С `nowrap` подпись обязана быть КОРОТКОЙ и предсказуемой — глаголом из
 * локали, а не данными. Единственное место, где сюда подставлялось название
 * курса, переведено на `L.ChipLink` (`CareerDictionaries/SiteCourses.tsx`).
 */
export const InlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border: 1px solid var(--k-border-2563eb);
  background: var(--k-bg-ffffff);
  color: var(--k-fg-2563eb);
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--k-bg-2563eb);
    color: var(--k-fg-ffffff);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const EmptyState = styled.div`
  padding: 56px 20px;
  text-align: center;
  color: var(--k-fg-5b6775);
  font-size: 14px;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
`;

export const EmptyHint = styled.div`
  margin-top: 8px;
  color: var(--k-fg-5b6775);
  font-size: 12px;
`;

export const CenterLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;

  & > div {
    width: 48px;
    height: 48px;
  }
`;

export const DimWhenFetching = styled.div<{ fetching: boolean }>`
  transition: opacity 0.15s;
  ${({ fetching }) =>
    fetching &&
    css`
      opacity: 0.55;
      pointer-events: none;
    `}
`;

/* ============= Detail page tokens ============= */

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const CardTitle = styled.div`
  /* Значок слева от подписи карточки — как в Zoom-разделе. Без flex глиф
     садится на базовую линию текста. */
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--k-fg-475569);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 12px;
`;

/**
 * Шапка карточки: `CardTitle` слева, действия (`CardHeadActions`) справа.
 * Отступ до содержимого — тот же, что у одиночного `CardTitle`; свой
 * `margin-bottom` заголовок внутри строки сбрасывает (без `style={{margin:0}}`).
 */
export const CardHeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  & > ${CardTitle} {
    margin-bottom: 0;
  }
`;

/** Действия в `CardHeadRow`. Рендерить, только когда в нём есть кнопки. */
export const CardHeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const Row = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  font-size: 13px;
  align-items: center;
`;

export const RowLabel = styled.div`
  color: var(--k-fg-64748b);
  flex: 0 0 160px;
`;

export const RowValue = styled.div`
  color: var(--k-fg-0f172a);
  font-weight: 500;
  flex: 1;
`;

export const HeadingPills = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin: 4px 0 12px;
`;

export const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ActionButton = styled.button<{
  tone?: 'primary' | 'danger' | 'ghost';
}>`
  /* Значок рядом с подписью: без flex глиф садится на базовую линию текста.
     В колоночном ActionsList кнопка по-прежнему во всю ширину, а подпись —
     по левому краю, как и раньше. */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid
    ${({ tone }) =>
      tone === 'danger'
        ? 'var(--k-border-fecaca)'
        : tone === 'primary'
        ? 'var(--k-border-2563eb)'
        : 'var(--k-border-e2e8f0)'};
  background: ${({ tone }) =>
    tone === 'primary' ? 'var(--k-bg-2563eb)' : 'var(--k-bg-ffffff)'};
  color: ${({ tone }) =>
    tone === 'primary'
      ? 'var(--k-fg-ffffff)'
      : tone === 'danger'
      ? 'var(--k-fg-991b1b)'
      : 'var(--k-fg-0f172a)'};
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${({ tone }) =>
      tone === 'primary'
        ? 'var(--k-bg-1d4ed8)'
        : tone === 'danger'
        ? 'var(--k-bg-fef2f2)'
        : 'var(--k-bg-f8fafc)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const RawPayloadToggle = styled.button`
  border: 0;
  background: transparent;
  color: var(--k-fg-2563eb);
  font-size: 12px;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const RawPre = styled.pre`
  font-family: ui-monospace, monospace;
  font-size: 11px;
  background: var(--k-bg-0f172a);
  color: var(--k-fg-e2e8f0);
  padding: 12px;
  border-radius: 8px;
  overflow: auto;
  max-height: 320px;
`;
